import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addSuccessorSessionTimerListener,
  clearSuccessorSessionTimer,
  getSuccessorSessionExpiresAt,
  getSuccessorSessionRemainingMs,
  resetSuccessorSessionTimer,
  SUCCESSOR_SESSION_DURATION_MS,
  setSuccessorSessionExpiresAt,
} from "utils/successorSessionTimer";

type UseSuccessorSessionTimerOptions = {
  enabled: boolean;
  showWhenRemainingMsAtMost?: number;
  onExpired?: () => void;
};

export const useSuccessorSessionTimer = ({
  enabled,
  showWhenRemainingMsAtMost = 2 * 60 * 1000,
  onExpired,
}: UseSuccessorSessionTimerOptions) => {
  const [expiresAtMs, setExpiresAtMs] = useState<number | null>(() =>
    getSuccessorSessionExpiresAt()
  );
  const [nowMs, setNowMs] = useState(() => Date.now());
  const expiredCalledRef = useRef(false);

  const remainingMs = useMemo(() => {
    if (!enabled) return 0;
    if (!expiresAtMs) return 0;
    return Math.max(0, expiresAtMs - nowMs);
  }, [enabled, expiresAtMs, nowMs]);

  const isVisible = enabled && remainingMs > 0 && remainingMs <= showWhenRemainingMsAtMost;

  const reset = useCallback(() => {
    expiredCalledRef.current = false;
    const newExpiresAt = resetSuccessorSessionTimer();
    setExpiresAtMs(newExpiresAt);
    return newExpiresAt;
  }, []);

  const setToDurationFromNow = useCallback((durationMs: number) => {
    expiredCalledRef.current = false;
    const newExpiresAt = Date.now() + durationMs;
    setSuccessorSessionExpiresAt(newExpiresAt);
    setExpiresAtMs(newExpiresAt);
    return newExpiresAt;
  }, []);

  const clear = useCallback(() => {
    clearSuccessorSessionTimer();
    setExpiresAtMs(null);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    const currentExpiresAt = getSuccessorSessionExpiresAt();
    if (!currentExpiresAt || currentExpiresAt <= Date.now()) {
      const newExpiresAt = resetSuccessorSessionTimer();
      setExpiresAtMs(newExpiresAt);
    } else {
      setExpiresAtMs(currentExpiresAt);
    }

    const unsubscribe = addSuccessorSessionTimerListener((next) => {
      setExpiresAtMs(next);
      if (next) expiredCalledRef.current = false;
    });

    return unsubscribe;
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;

    const interval = window.setInterval(() => {
      setNowMs(Date.now());

      const rem = getSuccessorSessionRemainingMs();
      if (rem <= 0) {
        if (!expiredCalledRef.current) {
          expiredCalledRef.current = true;
          onExpired?.();
        }
      }
      const nextExpiresAt = getSuccessorSessionExpiresAt();
      setExpiresAtMs((prev) => (prev === nextExpiresAt ? prev : nextExpiresAt));
    }, 1000);

    return () => window.clearInterval(interval);
  }, [enabled, onExpired]);

  const remainingSeconds = Math.ceil(remainingMs / 1000);

  const formatted = useMemo(() => {
    const totalSeconds = Math.max(0, remainingSeconds);
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const ss = String(totalSeconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  }, [remainingSeconds]);

  return {
    enabled,
    expiresAtMs,
    remainingMs,
    remainingSeconds,
    formatted,
    isVisible,
    reset,
    setToDurationFromNow,
    clear,
    durationMs: SUCCESSOR_SESSION_DURATION_MS,
  };
};
